import { useNavigate } from 'react-router-dom';
import {
  useAddCampaignMutation,
  useGetCampaignDictionariesQuery,
} from 'src/api/campaigns/hooks';
import { validateEmpty } from 'src/helpers/validators';
import { links } from 'src/router/routes';

export type FormData = {
  name: string;
  date_from: string;
  date_to: string;
  campaign_type: string;
  campaign_goal: string;
  location_type: string;
};

const initialValues: Partial<FormData> = {
  name: '',
  date_from: '',
  date_to: '',
  campaign_type: '',
  campaign_goal: '',
  location_type: '',
};

const validate = {
  name: (value: string) => validateEmpty(value, 'Please enter campaign name'),
  date_from: (value: string) =>
    validateEmpty(value, 'Please select a valid start date'),
  date_to: (value: string) =>
    validateEmpty(value, 'Please select a valid end date'),
  campaign_type: (value: string) =>
    validateEmpty(value, 'Please select campaign type'),
  campaign_goal: (value: string) =>
    validateEmpty(value, 'Please select campaign goal'),
  location_type: (value: string) =>
    validateEmpty(value, 'Please select location type'),
};

export const useAddCampaign = () => {
  const { addCampaignMutation, isPending } = useAddCampaignMutation();
  const { data: dictionaries, isLoading: isDictionariesLoading } =
    useGetCampaignDictionariesQuery();

  const navigate = useNavigate();

  const onSubmit = async (data: FormData) => {
    const dateFrom = new Date(data.date_from).toISOString();
    const dateTo = new Date(data.date_to).toISOString();

    await addCampaignMutation({
      name: data.name,
      date_from: dateFrom,
      date_to: dateTo,
      campaign_type: Number(data.campaign_type),
      campaign_goal: Number(data.campaign_goal),
      location_type: Number(data.location_type),
    });

    navigate(links.campaigns);
  };

  const onCancel = () => {
    navigate(links.campaigns);
  };

  const campaignTypeOptions =
    dictionaries?.campaign_types.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  const campaignGoalOptions =
    dictionaries?.campaign_goals.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  const locationTypeOptions =
    dictionaries?.location_types.map((item) => ({
      label: item.name,
      value: item.id.toString(),
    })) || [];

  return {
    isPending: isPending || isDictionariesLoading,
    onSubmit,
    onCancel,
    initialValues,
    validate,
    campaignTypeOptions,
    campaignGoalOptions,
    locationTypeOptions,
  };
};
